import React from "react";
import { Shield, GitFork, Cpu, FileText, CheckCircle2, AlertTriangle, Layers, Folder, Database, Lock, RefreshCw, Bot } from "lucide-react";

export default function FeatureDetailSection() {
  return (
    <section className="tfl-features" id="features" aria-label="Feature details">
      {/* ── Feature Row 1: Unified Threat Catalog & Workspace Dashboard ── */}
      <div className="tfl-frow">
        <div className="tfl-blob tfl-frow__blob" aria-hidden="true">
          <div className="tfl-blob__shape" />
        </div>

        <div className="tfl-frow__visual">
          <div className="tfl-app tfl-app--img">
            <img
              src="/assets/dashboard.png"
              alt="ThreatForge Unified Threat Catalog & Workspace Dashboard"
              className="tfl-app__img"
            />
          </div>
        </div>

        <div className="tfl-frow__copy">
          <div className="tfl-eyebrow">Centralized Threat Intelligence</div>
          <h2 className="tfl-display">
            Unified Threat Catalog &amp; Workspace Dashboard.
          </h2>
          <p className="tfl-body-lg">
            Manage all application threat models in one centralized command center. Filter threat inventories across STRIDE, PASTA, and MITRE ATT&amp;CK frameworks, track risk severity distributions, and monitor team-wide mitigation velocity in real time.
          </p>

          <ul className="tfl-tiles">
            <li className="tfl-tile">
              <div className="tfl-tile__name">
                <span className="tfl-tile__dot" />
                Multi-Framework Threat Filtering
              </div>
              <div className="tfl-tile__desc">
                Instantly slice threat inventories across STRIDE, PASTA 7-stage process, and 14 MITRE ATT&amp;CK tactics.
              </div>
            </li>
            <li className="tfl-tile">
              <div className="tfl-tile__name">
                <span className="tfl-tile__dot" />
                Real-Time Risk Posture Analytics
              </div>
              <div className="tfl-tile__desc">
                Track high, medium, and low severity risk distributions and mitigation rates across enterprise workspaces.
              </div>
            </li>
          </ul>
        </div>
      </div>

      {/* ── Feature Row 2: Dynamic Attack Tree Viewer ── */}
      <div className="tfl-frow tfl-frow--rev">
        <div className="tfl-blob tfl-frow__blob" aria-hidden="true">
          <div className="tfl-blob__shape tfl-blob__shape--2" />
        </div>

        <div className="tfl-frow__visual">
          <div className="tfl-app tfl-app--img">
            <video
              autoPlay
              loop
              muted
              playsInline
              className="tfl-app__video"
              src="/assets/videos/attack-tree.mp4"
            />
          </div>
        </div>

        <div className="tfl-frow__copy">
          <div className="tfl-eyebrow">Attack Simulation</div>
          <h2 className="tfl-display">
            Dynamic attack trees that evolve with your codebase.
          </h2>
          <p className="tfl-body-lg">
            Visualize complex multi-stage attack paths with AND/OR logic gates. ThreatForge calculates attack probability, adversary effort, and countermeasure placement automatically.
          </p>

          <ul className="tfl-tiles">
            <li className="tfl-tile">
              <div className="tfl-tile__name">
                <span className="tfl-tile__dot" />
                CAPEC &amp; ATT&amp;CK Leaf Node Mapping
              </div>
              <div className="tfl-tile__desc">
                Correlate leaf attack vectors directly with standardized adversary techniques and weakness IDs.
              </div>
            </li>
            <li className="tfl-tile">
              <div className="tfl-tile__name">
                <span className="tfl-tile__dot" />
                Interactive Path Tracing &amp; Logic Gates
              </div>
              <div className="tfl-tile__desc">
                Pinpoint single points of failure and test security controls prior to code deployment.
              </div>
            </li>
          </ul>
        </div>
      </div>

      {/* ── Feature Row 3: Enterprise Knowledge Base Spaces ── */}
      <div className="tfl-frow">
        <div className="tfl-blob tfl-frow__blob" aria-hidden="true">
          <div className="tfl-blob__shape" />
        </div>

        <div className="tfl-frow__visual">
          <div className="tfl-app">
            <div className="tfl-app__bar">
              <div className="tfl-app__label">ThreatForge / Enterprise Knowledge Base Spaces</div>
            </div>
            <div className="tfl-app__body">
              <div className="tfl-spaces-preview">
                <div className="tfl-spaces-card">
                  <div className="tfl-spaces-hdr">
                    <Database size={15} color="#c4b5fd" />
                    <span>AWS Production Spec &amp; IaC Space</span>
                    <span className="tfl-spaces-tag">SYNCED</span>
                  </div>
                  <div className="tfl-spaces-meta">34 Documents · 18 Architecture Schemas · Updated 5m ago</div>
                </div>

                <div className="tfl-spaces-card">
                  <div className="tfl-spaces-hdr">
                    <Lock size={15} color="#38bdf8" />
                    <span>PCI-DSS 4.0 &amp; ISO 27001 Security Guidelines</span>
                    <span className="tfl-spaces-tag tfl-spaces-tag--active">ACTIVE IN SESSION</span>
                  </div>
                  <div className="tfl-spaces-meta">12 Compliance Policies · 140 Security Rules</div>
                </div>

                <div className="tfl-spaces-card">
                  <div className="tfl-spaces-hdr">
                    <Folder size={15} color="#34d399" />
                    <span>Banking API Gateway Codebase Context</span>
                    <span className="tfl-spaces-tag">INDEXED</span>
                  </div>
                  <div className="tfl-spaces-meta">Terraform Modules · OpenAPI Specs · JWT Auth Guidelines</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="tfl-frow__copy">
          <div className="tfl-eyebrow">Context-Aware AI Intelligence</div>
          <h2 className="tfl-display">
            Grounded in your enterprise Knowledge Base Spaces.
          </h2>
          <p className="tfl-body-lg">
            Eliminate generic AI hallucinations. Attach IaC repositories, architecture specs, security guidelines, and compliance rules directly as dedicated Spaces, ensuring Sentry AI threat evaluations are tailored to your real environment.
          </p>

          <ul className="tfl-tiles">
            <li className="tfl-tile">
              <div className="tfl-tile__name">
                <span className="tfl-tile__dot" />
                IaC &amp; Specification Ingestion
              </div>
              <div className="tfl-tile__desc">
                Attach Terraform, CloudFormation, OpenAPI specs, and internal security guidelines directly to threat sessions.
              </div>
            </li>
            <li className="tfl-tile">
              <div className="tfl-tile__name">
                <span className="tfl-tile__dot" />
                Strict Context Scoping
              </div>
              <div className="tfl-tile__desc">
                Sentry AI evaluates threats exclusively within your verified enterprise boundaries and security policies.
              </div>
            </li>
          </ul>
        </div>
      </div>

      {/* ── Feature Row 4: Sentry AI Copilot ── */}
      <div className="tfl-frow tfl-frow--rev">
        <div className="tfl-blob tfl-frow__blob" aria-hidden="true">
          <div className="tfl-blob__shape tfl-blob__shape--2" />
        </div>

        <div className="tfl-frow__visual">
          <div className="tfl-app tfl-app--img tfl-app--sentry">
            <img
              src="/assets/sentry.png"
              alt="Sentry AI Copilot analyzing a threat model session"
              className="tfl-app__img"
            />
          </div>
        </div>

        <div className="tfl-frow__copy">
          <div className="tfl-eyebrow">
            <Bot size={16} color="#c4b5fd" />
            Sentry AI Copilot
          </div>
          <h2 className="tfl-display">
            Your threat catalog, editable by conversation.
          </h2>
          <p className="tfl-body-lg">
            Sentry is the in-product AI assistant connected directly to active threat model sessions. It reads architecture diagrams, decomposed assets, data flows, and trust boundaries — then identifies missing STRIDE coverage, recommends countermeasures, and writes straight into the catalog when you ask.
          </p>

          <ul className="tfl-tiles">
            <li className="tfl-tile">
              <div className="tfl-tile__name">
                <span className="tfl-tile__dot" />
                Reads Your Live Threat Model
              </div>
              <div className="tfl-tile__desc">
                Understands diagrams, data flows, trust boundaries, and the components already decomposed in the session.
              </div>
            </li>
            <li className="tfl-tile">
              <div className="tfl-tile__name">
                <span className="tfl-tile__dot" />
                Acts on the Catalog in Real Time
              </div>
              <div className="tfl-tile__desc">
                Adds threats, updates mitigations, and generates dynamic attack trees on demand — at sprint speed.
              </div>
            </li>
            <li className="tfl-tile">
              <div className="tfl-tile__name">
                <span className="tfl-tile__dot" />
                Every Step Auditable
              </div>
              <div className="tfl-tile__desc">
                Prompts, agent reasoning, and resulting catalog changes are logged side by side in the analysis trail.
              </div>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}
